import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {HelpIcon} from '@/components/help-icon'
import {PageHeading} from '@/components/page-heading'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {ListEmptyState, SectionCard} from '@/components/section-card'
import {SegmentedControl} from '@/components/segmented-control'
import {ESTADO_TONE, estadoChipLabel, gravedadChipLabel} from '@/components/incidentes/estado-linea'
import {PlanGate} from '@/components/plan-upsell'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useIncidentes, INCIDENTES_PAGE_SIZE, type IncidenteListItem} from '@/hooks/sgsst/incidentes/use-incidentes'
import {useIndicadores, type Indicador, type VentanaTendencia} from '@/hooks/sgsst/incidentes/use-investigacion'
import {formatShortDate} from '@/lib/risk'
import {ChevronGlyph} from '@/components/ui/icons'
import {cn} from '@/lib/utils'

/** Mes en que el evento entró a Kairos, no cuándo ocurrió (eso es `evento_fecha`). */
const mesRegistro = new Intl.DateTimeFormat('es-CO', {month: 'short', year: 'numeric'})

function semaforoTone(item: IncidenteListItem): 'red' | 'amber' | 'green' {
  if (item.algo_vencido) return 'red'
  if (item.algo_por_vencer) return 'amber'
  return 'green'
}

const RAIL: Record<'red' | 'amber' | 'green', string> = {
  red: 'border-l-tone-red-fg',
  amber: 'border-l-tone-amber-fg',
  green: 'border-l-primary',
}
const LEAD: Record<'red' | 'amber' | 'green', string> = {
  red: 'text-tone-red-fg',
  amber: 'text-tone-amber-fg',
  green: 'text-foreground',
}

function gravedadTone(gravedad: string): 'red' | 'amber' | 'neutral' {
  if (gravedad === 'mortal') return 'red'
  if (gravedad === 'grave') return 'amber'
  return 'neutral'
}

/** En accidentalidad subir es empeorar: la flecha sigue el signo del delta y el tono su efecto. */
function TendenciaDelta({indicador}: {indicador: Indicador}) {
  const {t} = useTranslation()
  const {tendencia, delta} = indicador
  const tone =
    tendencia === 'empeora'
      ? 'text-tone-red-fg'
      : tendencia === 'mejora'
        ? 'text-tone-green-fg'
        : 'text-muted-foreground'
  const flecha = tendencia === 'empeora' ? '↑' : tendencia === 'mejora' ? '↓' : '→'
  const magnitud = delta === null || delta === undefined ? null : Math.abs(delta)
  const texto =
    tendencia === 'estable' || magnitud === null || magnitud === 0
      ? t('investigacion.indicadores.tendencia.estable')
      : magnitud
  return (
    <span
      data-testid={`indicador-tendencia-${indicador.codigo}`}
      data-tendencia={tendencia}
      className={cn('inline-flex items-center gap-1 font-mono text-xs font-medium tabular-nums', tone)}>
      <span aria-hidden>{flecha}</span>
      <span className="sr-only">{t(`investigacion.indicadores.tendencia.sentido.${tendencia}`)}</span>
      <span>{texto}</span>
    </span>
  )
}

function IndicadorCard({indicador}: {indicador: Indicador}) {
  const {t} = useTranslation()
  const [showBase, setShowBase] = useState(false)
  return (
    <button
      type="button"
      data-testid={`indicador-${indicador.codigo}`}
      aria-expanded={showBase}
      onClick={() => setShowBase((v) => !v)}
      className="surface surface-raised flex flex-col p-4 text-left">
      <p className="flex items-start justify-between gap-2 font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        <span className="min-w-0">
          {indicador.label} · {indicador.estandar_0312}
        </span>
        <ChevronGlyph className={cn(showBase && 'rotate-180')} />
        <span className="sr-only">{t('common.verBaseLegal')}</span>
      </p>
      <div className="mt-2.5 flex items-baseline gap-2">
        <span className="font-mono text-[26px] leading-none font-medium tabular-nums">
          {indicador.valor === null ? '—' : indicador.valor}
        </span>
        <TendenciaDelta indicador={indicador} />
      </div>
      <p className="mt-2 font-mono text-[11px] text-muted-foreground tabular-nums">
        {t('investigacion.indicadores.formula', {
          num: indicador.numerador,
          den: indicador.denominador,
          k: indicador.k,
        })}
      </p>
      {showBase && <p className="mt-2 text-[11px] text-muted-foreground">{indicador.base_legal}</p>}
    </button>
  )
}

const VENTANAS: VentanaTendencia[] = ['semana', 'mes']

function IndicadoresPanel({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const [ventana, setVentana] = useState<VentanaTendencia>('semana')
  const {data} = useIndicadores(businessId, ventana)
  if (!data) return null
  return (
    <SectionCard
      testId="indicadores-panel"
      title={t('investigacion.indicadores.eyebrow', {year: data.periodo_year, n: data.n_trabajadores})}
      actions={
        <span className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            size="sm"
            testId="indicadores-ventana"
            ariaLabel={t('investigacion.indicadores.ventanaAria')}
            value={ventana}
            onChange={setVentana}
            options={VENTANAS.map((v) => ({
              key: v,
              testId: `indicadores-ventana-${v}`,
              label: t(`investigacion.indicadores.tendencia.${v}`),
            }))}
          />
          <HelpIcon
            label={t('common.help.iconLabelFor', {
              campo: t('investigacion.indicadores.eyebrow', {year: data.periodo_year, n: data.n_trabajadores}),
            })}
            testId="indicadores-tip">
            {t('investigacion.indicadores.tip')}
          </HelpIcon>
        </span>
      }
      bodyClassName="space-y-3 px-5 py-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.indicadores.map((ind) => (
          <IndicadorCard key={ind.codigo} indicador={ind} />
        ))}
      </div>
    </SectionCard>
  )
}

function IncidenteRow({item, businessId}: {item: IncidenteListItem; businessId?: string}) {
  const {t} = useTranslation()
  const tone = semaforoTone(item)
  return (
    <li className={cn('border-l-[3px]', RAIL[tone])}>
      <Link
        to={`/businesses/${businessId}/incidentes/${item.id}`}
        data-testid={`incidente-row-${item.id}`}
        className="group flex flex-wrap items-start justify-between gap-x-3 gap-y-2 px-4 py-3.5 transition-colors hover:bg-secondary/50">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <span
              className={cn('text-sm font-semibold', item.reloj_urgente_label ? LEAD[tone] : 'text-muted-foreground')}
              data-testid={`incidente-reloj-lead-${item.id}`}>
              {item.reloj_urgente_label ?? t('incidentes.list.sinReloj')}
            </span>
            {item.algo_vencido ? (
              <Tag tone="red">{t('incidentes.list.urgencia.vencido')}</Tag>
            ) : item.algo_por_vencer ? (
              <Tag tone="amber">{t('incidentes.list.urgencia.corriendo')}</Tag>
            ) : null}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{t(`incidentes.tipo.${item.tipo}`)}</span>
            {gravedadTone(item.gravedad_reportada) === 'neutral' ? (
              <span>{t(`incidentes.gravedad.${item.gravedad_reportada}`)}</span>
            ) : (
              <CeldaEstado
                chip={
                  <Tag tone={gravedadTone(item.gravedad_reportada)} data-testid={`incidente-gravedad-${item.id}`}>
                    {gravedadChipLabel(t, item.gravedad_reportada)}
                  </Tag>
                }
                porque={t(`incidentes.gravedad.${item.gravedad_reportada}`)}
              />
            )}
            {item.afectado_nombre && <span className="min-w-0 truncate">{item.afectado_nombre}</span>}
            <span className="font-mono tabular-nums">{formatShortDate(item.evento_fecha)}</span>
            <span className="font-mono tabular-nums" data-testid={`incidente-registrado-${item.id}`}>
              {t('incidentes.list.registrado', {mes: mesRegistro.format(new Date(item.created_at))})}
            </span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <CeldaEstado
            chip={<Tag tone={ESTADO_TONE[item.estado] ?? 'neutral'}>{estadoChipLabel(t, item.estado)}</Tag>}
            porque={t(`incidentes.estado.${item.estado}`)}
          />
          <ChevronGlyph size={16} className="-rotate-90 text-muted-foreground group-hover:translate-x-0.5" />
        </div>
      </Link>
    </li>
  )
}

const INCIDENTES_TABS = ['cola', 'indicadores'] as const
type IncidentesTab = (typeof INCIDENTES_TABS)[number]

export function IncidentesPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useIncidentes(businessId, undefined, page)
  const incidentes = data?.items
  const {can_use_incidentes} = useCapabilities(businessId)
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab: IncidentesTab = INCIDENTES_TABS.includes(tabParam as IncidentesTab) ? (tabParam as IncidentesTab) : 'cola'
  function setTab(next: IncidentesTab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }
  // Con la cola vacía el primario vive dentro del vacío; mientras carga no se ofrece en ninguno de los dos sitios.
  const colaVacia = isLoading || !incidentes || incidentes.length === 0

  // Un solo primario con un testid que viaja con él, para que el E2E no dependa de dónde esté.
  const reportarCta = () => (
    <PlanGate locked={!can_use_incidentes} feature={t('incidentes.list.reportar')} businessId={businessId}>
      <Button asChild data-testid="incidentes-reportar-cta">
        <Link to={`/businesses/${businessId}/incidentes/reportar`}>{t('incidentes.list.reportar')}</Link>
      </Button>
    </PlanGate>
  )

  return (
    <div className="space-y-5" data-testid="incidentes-page">
      <PageHeading title={t('incidentes.list.title')} accion={tab === 'cola' && !colaVacia && reportarCta()} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as IncidentesTab)}>
        <TabsList data-testid="incidentes-tabs">
          <TabsTrigger value="cola" data-testid="incidentes-tab-cola">
            {t('incidentes.list.tabs.cola')}
          </TabsTrigger>
          <TabsTrigger value="indicadores" data-testid="incidentes-tab-indicadores">
            {t('incidentes.list.tabs.indicadores')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cola" className="pt-4">
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !incidentes || incidentes.length === 0 ? (
            <div className="surface" data-testid="incidentes-empty">
              <ListEmptyState page title={t('incidentes.list.empty')} action={reportarCta()} />
            </div>
          ) : (
            // No pasa por `ListCard`: el riel de cada fila nace en el borde de la hoja y ese cuerpo es para `DataTable`.
            <section className="surface overflow-hidden" data-testid="incidentes-cola">
              <ul className="surface-divider divide-y">
                {incidentes.map((item) => (
                  <IncidenteRow key={item.id} item={item} businessId={businessId} />
                ))}
              </ul>
              {!!data?.total && (
                <div className="surface-divider border-t px-5 py-2">
                  <PaginationBar
                    page={page}
                    pageSize={INCIDENTES_PAGE_SIZE}
                    total={data.total}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </section>
          )}
        </TabsContent>

        <TabsContent value="indicadores" className="pt-4">
          {businessId && <IndicadoresPanel businessId={businessId} />}
        </TabsContent>
      </Tabs>
    </div>
  )
}
